import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  AlertTriangle, QrCode, Smartphone, Wifi, Shield, 
  Globe, ChevronDown, Heart, Clock, Check, Play
} from 'lucide-react';
import { translations, supportedLanguages, getTranslation } from '../utils/emergencyTranslations';
import { detectCurrency, getLocalizedPrice, PRICING } from '../utils/currency';

const EmergencyAppLanding = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showLangMenu, setShowLangMenu] = useState(false);
  
  // Currency detection
  const [localPrice, setLocalPrice] = useState(null);
  
  useEffect(() => {
    // Auto-detect currency on mount
    const price = getLocalizedPrice(PRICING.emergencyCard.price);
    setLocalPrice(price);
    
    // Set page title for standalone app
    document.title = 'My EmergencyCard App - Save a Life';
  }, []);
  
  // Get language from URL or browser
  const urlLang = searchParams.get('lang');
  const browserLang = navigator.language.split('-')[0];
  const defaultLang = urlLang || (translations[browserLang] ? browserLang : 'en');
  const [lang, setLang] = useState(defaultLang);
  
  const t = getTranslation(lang);

  const selectLanguage = (code) => {
    setLang(code);
    setShowLangMenu(false);
    // Update URL without reload
    window.history.replaceState({}, '', `?lang=${code}`);
  };

  const handleGetStarted = () => {
    // Use root path for standalone mode
    const basePath = window.location.pathname.includes('emergency-app') ? '/emergency-app/create' : '/create';
    navigate(`${basePath}?lang=${lang}`);
  };

  return (
    <div className={`min-h-screen bg-gradient-to-b from-[#D92B2B] via-#C02424 to-[#8B1818] ${t.direction === 'rtl' ? 'rtl' : 'ltr'}`}>
      {/* Header */}
      <header className="px-4 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-white">
          <img src="/logo.png" alt="Logo" className="h-8 w-8" />
          <span className="font-bold text-lg">{t.appName}</span>
        </div>
        
        {/* Language Selector */}
        <div className="relative">
          <Button
            variant="ghost"
            className="text-white hover:bg-white/20 gap-2"
            onClick={() => setShowLangMenu(!showLangMenu)}
          >
            <Globe className="h-4 w-4" />
            <span>{supportedLanguages.find(l => l.code === lang)?.flag}</span>
            <ChevronDown className="h-4 w-4" />
          </Button>
          
          {showLangMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
              {supportedLanguages.map((language) => (
                <button
                  key={language.code}
                  className={`w-full px-4 py-2 text-left flex items-center gap-3 hover:bg-slate-100 ${
                    lang === language.code ? 'bg-[#FEF2F2] text-[#C02424]' : ''
                  }`}
                  onClick={() => selectLanguage(language.code)}
                >
                  <span className="text-xl">{language.flag}</span>
                  <span className="text-sm">{language.name}</span>
                  {lang === language.code && <Check className="h-4 w-4 ml-auto text-[#D92B2B]" />}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <main className="px-4 pt-8 pb-12">
        <div className="text-center text-white max-w-md mx-auto">
          {/* QR Icon */}
          <div className="mb-6 inline-flex items-center justify-center w-24 h-24 rounded-full bg-white/20 backdrop-blur">
            <QrCode className="h-12 w-12 text-white" />
          </div>
          
          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-bold mb-4 leading-tight">
            {t.heroTitle}
          </h1>
          
          <p className="text-[#FEE2E2] text-lg mb-8">
            {t.heroSubtitle}
          </p>
          
          {/* CTA Button */}
          <Button
            onClick={handleGetStarted}
            className="w-full max-w-sm h-14 text-lg bg-white text-[#C02424] hover:bg-[#FEF2F2] shadow-xl"
            data-testid="get-started-btn"
          >
            {t.getStarted.replace('$1', localPrice?.formatted || '$1')}
          </Button>
          
          {/* How It Works Link */}
          <button
            onClick={() => navigate('/walkthrough')}
            className="mt-3 text-white/90 hover:text-white underline text-sm flex items-center justify-center gap-1 mx-auto"
            data-testid="how-it-works-link"
          >
            <Play className="h-4 w-4" />
            See how it works (1 min)
          </button>
          
          <p className="text-[#FECACA] text-sm mt-3">
            {localPrice ? (
              <>
                {localPrice.formatted} {t.oneTime}
                {localPrice.currency !== 'USD' && (
                  <span className="text-[#FCA5A5]/70 text-xs ml-1">
                    (~{localPrice.usdFormatted} USD)
                  </span>
                )}
              </>
            ) : (
              <>{t.price} {t.oneTime}</>
            )}
            {' '}• No subscription
          </p>
        </div>

        {/* Features */}
        <div className="mt-16 max-w-md mx-auto space-y-4">
          <Card className="bg-white/10 backdrop-blur border-0 text-white">
            <CardContent className="p-4 flex items-start gap-4">
              <div className="p-2 bg-white/20 rounded-lg">
                <Clock className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-bold">{t.feature1Title}</h3>
                <p className="text-[#FECACA] text-sm">{t.feature1Desc}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white/10 backdrop-blur border-0 text-white">
            <CardContent className="p-4 flex items-start gap-4">
              <div className="p-2 bg-white/20 rounded-lg">
                <Smartphone className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-bold">{t.feature2Title}</h3>
                <p className="text-[#FECACA] text-sm">{t.feature2Desc}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white/10 backdrop-blur border-0 text-white">
            <CardContent className="p-4 flex items-start gap-4">
              <div className="p-2 bg-white/20 rounded-lg">
                <Wifi className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-bold">{t.feature3Title}</h3>
                <p className="text-[#FECACA] text-sm">{t.feature3Desc}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* How It Works */}
        <div className="mt-16 max-w-md mx-auto text-center text-white">
          <h2 className="text-xl font-bold mb-8">How It Works</h2>
          
          <div className="flex items-center justify-center gap-2 md:gap-4">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mb-2">
                <span className="text-lg font-bold">1</span>
              </div>
              <span className="text-xs text-[#FECACA]">Fill Info</span>
            </div>
            
            <div className="w-8 h-0.5 bg-white/30"></div>
            
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mb-2">
                <span className="text-lg font-bold">2</span>
              </div>
              <span className="text-xs text-[#FECACA]">Get QR</span>
            </div>
            
            <div className="w-8 h-0.5 bg-white/30"></div>
            
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mb-2">
                <span className="text-lg font-bold">3</span>
              </div>
              <span className="text-xs text-[#FECACA]">Save</span>
            </div>
            
            <div className="w-8 h-0.5 bg-white/30"></div>
            
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mb-2">
                <Heart className="h-5 w-5" />
              </div>
              <span className="text-xs text-[#FECACA]">Stay Safe</span>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-16 max-w-md mx-auto flex justify-center gap-6 text-white/80">
          <div className="flex items-center gap-1 text-sm">
            <Shield className="h-4 w-4" />
            <span>No Account Needed</span>
          </div>
          <div className="flex items-center gap-1 text-sm">
            <Globe className="h-4 w-4" />
            <span>10+ Languages</span>
          </div>
        </div>

        {/* Second CTA */}
        <div className="mt-12 max-w-md mx-auto">
          <Button
            onClick={handleGetStarted}
            className="w-full h-12 bg-white text-[#C02424] hover:bg-[#FEF2F2]"
          >
            {t.getStarted}
          </Button>
        </div>
        
        {/* Cross-promotion to Full Health App */}
        <div className="mt-8 text-center">
          <p className="text-[#FECACA] text-sm mb-2">Want full health tracking?</p>
          <div 
            className="inline-flex items-center gap-2 text-white/80 text-sm font-medium"
          >
            <Heart className="h-4 w-4" />
            Full Health Passport — Coming Soon
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="px-4 py-6 text-center text-[#FECACA] text-sm border-t border-white/10">
        <div className="flex justify-center gap-4 mb-3">
          <a href={window.location.pathname.includes('emergency-app') ? '/emergency-app/privacy' : '/privacy'} className="hover:text-white">{t.privacyPolicy}</a>
          <span>•</span>
          <a href={window.location.pathname.includes('emergency-app') ? '/emergency-app/terms' : '/terms'} className="hover:text-white">{t.termsOfService}</a>
          <span>•</span>
          <a href={window.location.pathname.includes('emergency-app') ? '/emergency-app/contact' : '/contact'} className="hover:text-white">Contact</a>
        </div>
        <p className="text-[#FCA5A5]/60 text-xs">
          Own Your Data - Your info never leaves your device
        </p>
        <p className="text-[#FCA5A5]/40 text-xs mt-1">© 2026 My Emergency Card</p>
      </footer>
    </div>
  );
};

export default EmergencyAppLanding;
